import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ActionList } from "../../components/ui";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import SortIcon from "../../components/ui/Sorting";
import SortSummary from "../../components/ui/SortSummary";
import { formatDateTime } from "../../helpers/formatDateTime";
import { BuildingDetailsPanel } from "./BuildingDetailsPanel";
import type { CustomerWithSite } from "./buildingRules";
import type { CustomerDetails } from "./useBuildings";

const COLUMNS = [
  { key: "customerName", label: "Name" },
  { key: "createdAt", label: "Created" },
  { key: "rules", label: "Rules" },
  { key: "sites", label: "Sites" },
] as const;
type SortColumn = typeof COLUMNS[number]["key"];

interface Props {
  customers: CustomerWithSite[];
  details: Record<number, CustomerDetails>;
  detailsLoading: Record<number, boolean>;
  loading: boolean;
  editing: boolean;
  isAdmin: boolean;
  onEdit: (customer: CustomerWithSite) => void;
  onDelete: (customer: CustomerWithSite) => void;
}

export function BuildingList({ customers, details, detailsLoading, loading, editing, isAdmin, onEdit, onDelete }: Props) {
  const [sortColumn, setSortColumn] = useState<SortColumn>("customerName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [hasAppliedSort, setHasAppliedSort] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const activeColumn = COLUMNS.find(column => column.key === sortColumn);
  const sortedCustomers = useMemo(() => {
    const value = (customer: CustomerWithSite): string | number => {
      switch (sortColumn) {
        case "customerName": return (customer.customerName || "").toLowerCase();
        case "createdAt": return new Date(customer.createdAt || 0).getTime();
        case "rules": return details[customer.id]?.rules.length ?? 0;
        case "sites": return details[customer.id]?.sites.length ?? 0;
      }
    };
    return [...customers].sort((left, right) => {
      const first = value(left);
      const second = value(right);
      return (first < second ? -1 : first > second ? 1 : 0) * (sortOrder === "asc" ? 1 : -1);
    });
  }, [customers, details, sortColumn, sortOrder]);
  useEffect(() => {
    if (!sortedCustomers.some(customer => customer.id === selectedId)) {
      setSelectedId(sortedCustomers[0]?.id ?? null);
    }
  }, [sortedCustomers, selectedId]);
  const selectedCustomer = sortedCustomers.find(customer => customer.id === selectedId);

  const sort = (column: SortColumn) => {
    setHasAppliedSort(true);
    setSortColumn(column);
    setSortOrder(previous => sortColumn === column && previous === "asc" ? "desc" : "asc");
  };
  const reset = () => {
    setSortColumn("customerName");
    setSortOrder("asc");
    setHasAppliedSort(false);
  };

  return (
    <>
      <div className="alarm-content--heading" aria-hidden={editing}>
        <div>
          <h3 className="heading heading__title--building">Customer Details</h3>
          {hasAppliedSort && activeColumn && (
            <SortSummary columnLabel={activeColumn.label} sortOrder={sortOrder} onReset={reset}
              resetAriaLabel="Reset sorting to name ascending" />
          )}
        </div>
      </div>
      {loading && <LoadingSpinner message="Loading customers…" />}
      {!loading && (
        <div className="admin-requests">
          <div className="admin-requests__table">
            <table className="table">
              <thead><tr>
                {COLUMNS.map(column => (
                  <th key={column.key} onClick={() => sort(column.key)} className="th--sortable"
                    aria-sort={sortColumn === column.key ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}>
                    {column.label} <SortIcon sortOrder={sortColumn === column.key ? sortOrder : null} />
                  </th>
                ))}
                <th>Actions</th>
              </tr></thead>
              <tbody>
                {sortedCustomers.map(customer => (
                  <tr key={customer.id} onClick={() => setSelectedId(customer.id)}
                    className={`admin-request-row${selectedId === customer.id ? " admin-request-row--selected" : ""}`}>
                    <td><Link className="alarm-name-link" to={`/customers/customer-${customer.id}`} onClick={event => event.stopPropagation()}>{customer.customerName}</Link></td>
                    <td>{formatDateTime(customer.createdAt)}</td>
                    <td>{details[customer.id]?.rules.length ?? "..."}</td>
                    <td>{details[customer.id]?.sites.length ?? "..."}</td>
                    <td>
                      <div onClick={event => event.stopPropagation()} onKeyDown={event => {
                        if (event.key === "Enter" || event.key === " ") event.stopPropagation();
                      }}>
                        <ActionList onEdit={() => onEdit(customer)}
                          onDelete={details[customer.id]?.sites.length === 0 ? () => onDelete(customer) : undefined} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="admin-requests__panel">
            {selectedCustomer ? (
              <BuildingDetailsPanel customer={selectedCustomer} details={details[selectedCustomer.id]}
                loading={detailsLoading[selectedCustomer.id] ?? false} canDelete={isAdmin} onEdit={onEdit} onDelete={onDelete} />
            ) : <div>Select a customer to view details.</div>}
          </div>
        </div>
      )}
    </>
  );
}
